// Aignoz Phase 5 — Login / Signup gate.
// Full-bleed hero image · dark-glass card · "WHO ARE YOU?" persona picker.
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ArrowRight, Sparkles, User, Rocket, TrendingUp, Microscope, Stethoscope, Building2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/auth-context";

const MAGENTA = "#D17EC0";

const PERSONAS = [
  { key: "founder",          label: "Founder",           icon: Rocket,       hint: "Build a venture" },
  { key: "investor",         label: "Investor",          icon: TrendingUp,   hint: "Find deal flow" },
  { key: "researcher",       label: "Researcher",        icon: Microscope,   hint: "Map biology" },
  { key: "clinician",        label: "Clinician",         icon: Stethoscope,  hint: "Solve unmet need" },
  { key: "incubator",        label: "Incubator",         icon: Building2,    hint: "Score cohorts" },
  { key: "enterprise_admin", label: "Enterprise Admin",  icon: ShieldCheck,  hint: "Deploy to R&D" },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, demoLogin } = useAuth();

  const [mode, setMode] = useState("login"); // "login" | "register"
  const [persona, setPersona] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [skipBusy, setSkipBusy] = useState(false);
  const [error, setError] = useState(null);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const pwValid = password.length >= 6;
  const canSubmit = persona && emailValid && pwValid && (mode === "login" || name.trim().length >= 1);

  const redirectAfter = () => {
    const dest = location.state?.from || "/";
    navigate(dest, { replace: true });
  };

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true); setError(null);
    const call = mode === "login"
      ? login({ email: email.trim().toLowerCase(), password, persona })
      : register({ email: email.trim().toLowerCase(), password, persona, name: name.trim() });
    const r = await call;
    if (!r.ok) {
      setError(r.error);
      toast.error(mode === "login" ? "Login failed" : "Sign up failed", { description: r.error });
      setBusy(false);
      return;
    }
    // The backend already applied the persona override during login/register; no client PATCH needed.
    toast.success(mode === "login" ? "Welcome back" : "Account created", {
      description: `Entering as ${(PERSONAS.find(p => p.key === persona) || {}).label || persona}`,
    });
    setBusy(false);
    redirectAfter();
  }

  async function onSkip() {
    setSkipBusy(true); setError(null);
    const r = await demoLogin();
    if (!r.ok) {
      toast.error("Demo entry failed", { description: r.error });
      setSkipBusy(false);
      return;
    }
    toast("Demo mode", { description: "Read-only entry as founder." });
    setSkipBusy(false);
    redirectAfter();
  }

  const selected = PERSONAS.find((p) => p.key === persona);

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="login-page"
      style={{
        backgroundImage: "url(/aignoz-login-bg.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Vignette overlay — darken corners for legibility */}
      <div
        data-testid="login-bg-hero"
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)",
        }}
      />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top-left brand */}
        <header className="p-6 flex items-center gap-2">
          <img src="/aignoz-logo.png" alt="Aignoz" className="h-7 w-7 object-contain opacity-90" />
          <span className="text-[11px] uppercase tracking-[0.22em] text-white/60 mono">Aignoz Innovation OS™</span>
        </header>

        {/* Centered card */}
        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="w-full max-w-[480px] rounded-2xl backdrop-blur-xl border border-white/[0.10] p-8 sm:p-10"
            style={{ background: "rgba(11,12,16,0.55)" }}
          >
            <div className="text-center mb-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mono mb-2">
                Aignoz Idea Factory
              </div>
              <h1
                className="text-3xl sm:text-4xl font-semibold tracking-[0.15em] uppercase"
                style={{ color: MAGENTA }}
              >
                Who are you?
              </h1>
              <p className="text-[13px] text-white/60 italic mt-2 leading-snug max-w-[36ch] mx-auto">
                Pick your lens. The OS adapts to how you see healthcare.
              </p>
            </div>

            {/* Persona chips 2×3 */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
              {PERSONAS.map((p) => {
                const isSel = p.key === persona;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPersona(p.key)}
                    data-testid={`login-persona-${p.key}`}
                    className={`rounded-lg border px-3 py-2.5 text-left transition ${
                      isSel
                        ? "border-[rgba(209,126,192,0.65)] bg-[rgba(209,126,192,0.10)] text-white shadow-[0_0_0_3px_rgba(209,126,192,0.15)]"
                        : "border-white/[0.10] bg-white/[0.02] text-white/65 hover:border-white/[0.20] hover:text-white/90"
                    }`}
                    style={isSel ? { color: MAGENTA } : {}}
                  >
                    <div className="flex items-center gap-1.5">
                      <p.icon className="w-3.5 h-3.5" />
                      <span className="text-[12px] font-medium">{p.label}</span>
                    </div>
                    <div className="text-[10px] text-white/45 mt-0.5 leading-tight">{p.hint}</div>
                  </button>
                );
              })}
            </div>

            {/* Form */}
            <form onSubmit={onSubmit} className="space-y-3">
              <AnimatePresence initial={false}>
                {mode === "register" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}
                  >
                    <FloatingInput
                      testid="login-name" label="Name" type="text"
                      value={name} onChange={setName} autoComplete="name" required
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <FloatingInput
                testid="login-email" label="Email" type="email"
                value={email} onChange={setEmail} autoComplete="email" required
              />
              <FloatingInput
                testid="login-password" label="Password" type="password"
                value={password} onChange={setPassword} autoComplete={mode === "login" ? "current-password" : "new-password"}
                required minLength={6}
                hint={password && !pwValid ? "min 6 chars" : null}
              />

              {error && (
                <div className="text-[11px] text-rose-300 bg-rose-500/[0.08] border border-rose-400/25 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={!canSubmit || busy}
                data-testid="login-submit"
                className="w-full h-11 gap-2 text-black font-medium disabled:opacity-40"
                style={{ background: MAGENTA }}
              >
                {busy
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <ArrowRight className="w-4 h-4" />}
                Enter as {selected ? selected.label : "…"}
              </Button>

              <button
                type="button"
                onClick={() => { setMode((m) => (m === "login" ? "register" : "login")); setError(null); }}
                data-testid="login-mode-toggle"
                className="w-full text-[11px] text-white/55 hover:text-white text-center"
              >
                {mode === "login"
                  ? "Don't have an account? · Sign up"
                  : "Already have an account? · Log in"}
              </button>

              <div className="border-t border-white/[0.06] my-3" />

              <button
                type="button"
                onClick={onSkip}
                disabled={skipBusy}
                data-testid="login-skip"
                className="w-full h-9 text-[11px] uppercase tracking-[0.16em] text-white/55 hover:text-white inline-flex items-center justify-center gap-1.5"
              >
                {skipBusy
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Sparkles className="w-3 h-3" />}
                Skip · Enter demo mode
              </button>
            </form>
          </motion.div>
        </main>

        {/* Founder footer strip */}
        <footer className="p-5 text-center">
          <div className="text-[10px] text-white/45 mono">
            <span className="text-white/75">Dr. Sriinidhi BV</span> · MBBS, MD · Founder &amp; CEO, Aignoz Healthcare Solutions ·{" "}
            <a href="https://www.aignoz.com/" target="_blank" rel="noreferrer" className="underline hover:text-white/80">aignoz.com</a>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function FloatingInput({ testid, label, type, value, onChange, autoComplete, required, minLength, hint }) {
  const [focus, setFocus] = useState(false);
  const active = focus || !!value;
  return (
    <div className="relative">
      <label
        className={`absolute left-3 transition-all pointer-events-none ${
          active ? "top-1 text-[9px] uppercase tracking-[0.14em]" : "top-3 text-[13px]"
        }`}
        style={{ color: active ? MAGENTA : "rgba(255,255,255,0.4)" }}
      >
        {label}
      </label>
      <Input
        data-testid={testid}
        type={type}
        value={value}
        required={required}
        minLength={minLength}
        autoComplete={autoComplete}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 pt-4 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/40"
        style={{
          borderColor: focus ? "rgba(209,126,192,0.55)" : undefined,
          boxShadow: focus ? "0 0 0 3px rgba(209,126,192,0.10)" : undefined,
        }}
      />
      {hint && <div className="text-[10px] text-white/40 mt-1 ml-1">{hint}</div>}
    </div>
  );
}
